import { AiResult } from '../../../../../../models/AiResult'
import { useCallback, useEffect, useState } from 'react'
import { CommentRelationType } from '../../../../../../models/MeetingSupport'
import { replaceFormattedResultOnPath, replaceResultOnPath } from './utils'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { stopEditingAiResult } from '../../../../../../store/meetingSummary/display'
import { useOverrideAiResult } from '../../hooks/api'
import { clearSavedComment, selectSavedComment } from '../../../../../../store/meetingSummary/meetingSupport'
import { TrackEvent, useTracking } from '../../../common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../../tracking'
import { AiFeature } from '../../../../../../models/AiFeature'
import { useMeetingSummary } from '../../MeetingSummaryAgent/hooks'
import { removeEditedAiResult, setEditedAiResult } from '../../../../../../store/meetingSummary/aiResults'

export interface EditingAiResult {
  aiResult: AiResult
  editingAiResult: AiResult
  handleResultTextContentChanged: (basePath: string, path: string, changedResult: string | boolean) => void
  handleFormattedResultChanged: (basePath: string, path: string, contentType: string, content: string) => void
  handleReadOnlyFormattedResultChanged: (basePath: string, path: string, contentType: string, content: string) => void
  handleCancel: () => void
  handleSave: () => Promise<void>
}
export const useEditingAiResult = (aiFeature: AiFeature, displayedAiResult: AiResult): EditingAiResult => {
  const dispatch = useAppDispatch()

  const { trackingClient } = useTracking()

  const savedComment = useAppSelector(selectSavedComment)

  const overrideAiResult = useOverrideAiResult()
  const { handleMeetingSummaryResultEdited } = useMeetingSummary()

  const [aiResult, setAiResult] = useState<AiResult>(displayedAiResult)
  const [editingAiResult, setEditingAiResult] = useState<AiResult>(displayedAiResult)
  const [edited, setEdited] = useState<boolean>(false)

  useEffect(() => {
    setAiResult(displayedAiResult)
    setEditingAiResult(displayedAiResult)
  }, [displayedAiResult])

  useEffect(() => {
    if (edited) {
      dispatch(setEditedAiResult(editingAiResult))
    }
  }, [dispatch, edited, editingAiResult])

  useEffect(() => {
    if (!savedComment) {
      return
    }
    const { relationType, relationId } = savedComment
    if (relationType === CommentRelationType.AI_RESULT && relationId === aiResult.id) {
      overrideAiResult(aiResult.id, { result: aiResult.result })
      dispatch(clearSavedComment())
    }
  }, [dispatch, savedComment, overrideAiResult, aiResult])

  const handleResultTextContentChanged = useCallback((basePath: string, path: string, changedResult: string | boolean) => {
    setEdited(true)
    setEditingAiResult((editingAiResult) => {
      const newResult = replaceResultOnPath(editingAiResult, basePath, path, changedResult)
      return { ...editingAiResult, result: newResult }
    })
  }, [])

  const handleFormattedResultChanged = useCallback((basePath: string, path: string, contentType: string, content: string) => {
    setEdited(true)
    setEditingAiResult((editingAiResult) => {
      const newResult = replaceFormattedResultOnPath(editingAiResult, basePath, path, contentType, content)
      return { ...editingAiResult, result: newResult }
    })
  }, [])

  const handleReadOnlyFormattedResultChanged = useCallback((basePath: string, path: string, contentType: string, content: string) => {
    setAiResult((aiResult) => {
      const newResult = replaceFormattedResultOnPath(aiResult, basePath, path, contentType, content)
      return { ...aiResult, result: newResult }
    })
    setEditingAiResult((editingAiResult) => {
      const clonedResult = replaceFormattedResultOnPath(editingAiResult, basePath, path, contentType, content)
      return { ...editingAiResult, result: clonedResult }
    })
  }, [])

  const trackOverrideEvent = useCallback(
    (actionStatus: string) => {
      trackingClient.track({
        eventName: TrackEvent.AI_RESULT_OVERRIDE,
        eventDetails: {
          ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
          actionStatus,
        },
      })
    },
    [trackingClient, aiFeature, aiResult],
  )

  const handleCancel = () => {
    const { id: aiResultId } = editingAiResult
    trackOverrideEvent('canceled')
    dispatch(stopEditingAiResult(aiResultId))
    dispatch(removeEditedAiResult(aiResultId))
  }

  const handleSave = async () => {
    setAiResult(editingAiResult)
    const { id: aiResultId, result } = editingAiResult
    await overrideAiResult(aiResultId, { result }, () => handleMeetingSummaryResultEdited(aiFeature, aiResultId, result))
    trackOverrideEvent('finished')
    dispatch(stopEditingAiResult(aiResultId))
    dispatch(removeEditedAiResult(aiResultId))
  }

  return {
    aiResult,
    editingAiResult,
    handleResultTextContentChanged,
    handleFormattedResultChanged,
    handleReadOnlyFormattedResultChanged,
    handleCancel,
    handleSave,
  }
}
